/**
 * React Error Boundary
 *
 * Catches runtime errors in the component tree and renders a fallback UI
 * instead of crashing the entire application.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <MyComponent />
 *   </ErrorBoundary>
 *
 *   // With custom fallback:
 *   <ErrorBoundary fallback={<MyFallback />}>
 *     <MyComponent />
 *   </ErrorBoundary>
 */

import React from 'react';
import { captureException } from '../../utils/sentry';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });

    // Forward to Sentry (no-op when VITE_SENTRY_DSN is unset)
    captureException(error, { componentStack: errorInfo?.componentStack });
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    if (this.state.hasError) {
      // Allow consumer to provide their own fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: '#0f172a',
            color: '#f1f5f9',
            borderRadius: '0.5rem',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Something went wrong
          </h2>
          <p style={{ color: '#94a3b8', marginBottom: '1.5rem', maxWidth: '400px' }}>
            An unexpected error occurred. The issue has been logged.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre
              style={{
                fontSize: '0.75rem',
                backgroundColor: '#1e293b',
                padding: '1rem',
                borderRadius: '0.375rem',
                maxWidth: '100%',
                overflow: 'auto',
                textAlign: 'left',
                color: '#f87171',
                marginBottom: '1.5rem',
              }}
            >
              {this.state.error.toString()}
              {this.state.errorInfo?.componentStack}
            </pre>
          )}
          <button
            onClick={this.handleReset}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
